/**
 * AI Chat Widget — GE Lines Pte Ltd
 *
 * Floating AI assistant bubble — available on all public + portal pages.
 * Powered by /api/selfservice/chat (falls back to demo responses if API unavailable).
 */

import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Loader2, Anchor } from 'lucide-react';
import { API_BASE } from '@/lib/api-base';
import { useStorefront } from '@/context/StorefrontContext';
import { useSurfaceAccess } from '@/context/SurfaceAccessContext';

interface Message {
  id: string;
  role: 'assistant' | 'user';
  content: string;
  ts: Date;
}

function buildDemoResponses(brand: {
  brandShortName: string;
  supportEmail: string;
  primaryPhone: string;
  secondaryPhone: string;
  addressLines: string[];
}) {
  return {
    default: `Hi there! I'm ${brand.brandShortName} AI assistant. I can help you with freight quotes, booking tracking, customs documentation, and general enquiries. How can I assist you today?`,
    track: `To track your booking, you can:\n• Use the **Track Booking** page and enter your B/L number, container number, or booking reference\n• Log in to your **Customer Portal** for full booking history\n• Call us at **${brand.primaryPhone}** for urgent enquiries\n\nWhat's your B/L, container number, or booking reference?`,
    quote: `For a freight quote, I'll need a few details:\n• **Origin & destination** (port/city)\n• **Cargo type** — FCL, LCL, or Air\n• **Container size** if FCL\n• **Cargo dimensions/weight** if LCL\n• **Ready date**\n\nYou can also email us at **${brand.supportEmail}** or call **${brand.primaryPhone}**.`,
    sea: `${brand.brandShortName} offers comprehensive **Sea Freight** services:\n• **FCL** — Full Container Load\n• **LCL** — Less than Container Load consolidation\n• **NVOCC** support\n• Regional and international routing\n\nWould you like a sea freight quote?`,
    air: `Our **Air Freight** services include:\n• Express & standard air cargo\n• Door-to-door delivery\n• Worldwide destinations\n\nAir freight is ideal for time-sensitive or high-value cargo. Need a quote?`,
    contact: `You can reach ${brand.brandShortName} at:\n\n📞 **${brand.primaryPhone}**\n📠 **${brand.secondaryPhone || brand.primaryPhone}**\n✉️ **${brand.supportEmail}**\n📍 **${brand.addressLines.join(', ')}**`,
    customs: `${brand.brandShortName} provides full **Customs & Documentation** support:\n• Import/export permit application\n• Customs clearance\n• Certificate of Origin\n• DG documentation\n• MSDS verification`,
    network: `${brand.brandShortName} supports customer booking visibility, rate enquiries, and booking coordination. Let us know your origin/destination and we'll guide you to the right service.`,
  };
}

function getDemoResponse(msg: string, responses: ReturnType<typeof buildDemoResponses>): string {
  const lower = msg.toLowerCase();
  if (/track|b\/l|bl |container|booking ref/i.test(lower)) return responses.track;
  if (/quote|rate|price|cost|how much/i.test(lower)) return responses.quote;
  if (/sea|ocean|fcl|lcl|vessel|ship\b/i.test(lower)) return responses.sea;
  if (/air|airfreight|plane|cargo\b/i.test(lower)) return responses.air;
  if (/contact|phone|email|address|office|location/i.test(lower)) return responses.contact;
  if (/custom|permit|tradenet|clearance|doc/i.test(lower)) return responses.customs;
  if (/network|country|office|location|southeast/i.test(lower)) return responses.network;
  return "Thanks for your message! Our team will get back to you shortly.\n\nI can also help with tracking, freight quotes, customs docs, and general enquiries.";
}

function MessageBubble({ msg }: { msg: Message }) {
  const isBot = msg.role === 'assistant';
  return (
    <div className={`flex gap-2.5 ${isBot ? 'items-start' : 'items-start flex-row-reverse'}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
        isBot ? 'bg-brand-700' : 'bg-slate-200'
      }`}>
        {isBot
          ? <Anchor className="w-3.5 h-3.5 text-white" />
          : <User className="w-3.5 h-3.5 text-slate-600" />
        }
      </div>
      <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
        isBot
          ? 'bg-slate-100 text-slate-800 rounded-tl-sm'
          : 'bg-brand-700 text-white rounded-tr-sm'
      }`}>
        {msg.content.split('\n').map((line, i) => {
          // Basic **bold** rendering
          const parts = line.split(/\*\*(.*?)\*\*/g);
          return (
            <p key={i} className={i > 0 ? 'mt-1' : ''}>
              {parts.map((part, j) =>
                j % 2 === 1 ? <strong key={j}>{part}</strong> : part
              )}
            </p>
          );
        })}
      </div>
    </div>
  );
}

export function AiChatWidget() {
  const { brand, host, featureFlags } = useStorefront();
  const { ensureAccess, buildHeaders, surfaceUser } = useSurfaceAccess();
  if (featureFlags.aiChat === false) return null;
  const demoResponses = buildDemoResponses(brand);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '0',
      role: 'assistant',
      content: demoResponses.default,
      ts: new Date(),
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  useEffect(() => {
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0]?.id !== '0') return prev;
      return [{ ...prev[0], content: demoResponses.default }];
    });
  }, [demoResponses.default]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const allowed = await ensureAccess('chat');
    if (!allowed) return;
    setInput('');

    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, ts: new Date() };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/selfservice/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...buildHeaders() },
        body: JSON.stringify({ message: text, sessionId: sessionIdRef.current, host }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error('API unavailable');
      const data = await res.json() as { ok: boolean; data?: { reply?: string; sessionId?: string } };
      const replyText = data?.data?.reply ?? getDemoResponse(text, demoResponses);
      if (data?.data?.sessionId) sessionIdRef.current = data.data.sessionId;
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: replyText,
        ts: new Date(),
      }]);
    } catch {
      // Fallback to demo response
      await new Promise(r => setTimeout(r, 600));
      setMessages(prev => [...prev, {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: getDemoResponse(text, demoResponses),
        ts: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const quickReplies = ['Get a Quote', 'New Booking', 'Track Booking', 'Contact Us'];

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-50 w-[360px] max-w-[calc(100vw-2rem)] flex flex-col bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-brand-800 to-ocean-600 px-4 py-3.5 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm leading-none">{brand.brandShortName} Assistant</p>
              <p className="text-white/70 text-xs mt-0.5">
                {surfaceUser ? `${surfaceUser.company} verified` : 'Email OTP required for live assistance'}
              </p>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="ml-auto w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[280px] max-h-[380px] bg-slate-50">
            {messages.map(msg => (
              <MessageBubble key={msg.id} msg={msg} />
            ))}
            {loading && (
              <div className="flex items-center gap-2.5">
                <div className="w-7 h-7 rounded-full bg-brand-700 flex items-center justify-center">
                  <Anchor className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-slate-100 px-3.5 py-2.5 rounded-2xl rounded-tl-sm">
                  <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
                </div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Quick replies */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 border-t border-slate-100 flex gap-2 flex-wrap bg-white">
              {quickReplies.map(q => (
                <button
                  key={q}
                  onClick={() => { setInput(q); }}
                  className="text-xs px-3 py-1.5 rounded-full bg-brand-50 text-brand-700 hover:bg-brand-100 transition-colors border border-brand-200"
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-4 py-3 border-t border-slate-100 bg-white flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder="Type a message…"
              className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-brand-400 bg-slate-50"
            />
            <button
              onClick={send}
              disabled={!input.trim() || loading}
              className="w-10 h-10 rounded-xl bg-brand-700 hover:bg-brand-800 disabled:opacity-40 flex items-center justify-center transition-colors"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-brand-700 to-ocean-500 shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
        aria-label="Open AI Chat"
      >
        {open
          ? <X className="w-6 h-6 text-white" />
          : <MessageCircle className="w-6 h-6 text-white" />
        }
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-500 rounded-full border-2 border-white" />
        )}
      </button>
    </>
  );
}
